/**
 * What makes a page a touch page (ADR-061's amendment of 2026-10-31, decision 3): no hover and a coarse pointer, a
 * phone or a tablet with no mouse. One function gates touch controls and the phone scale; it never removes a
 * desktop path.
 */

/**
 * The media query of a touch page.
 */
export const TOUCH_QUERY = "(hover: none) and (pointer: coarse)";

/**
 * The part of a window the touch page's question needs.
 */
export interface MediaWindow {
  readonly matchMedia?: (query: string) => Pick<MediaQueryList, "matches" | "addEventListener" | "removeEventListener">;
}

/**
 * Whether the page is a touch page now. Where there is no window or no `matchMedia` to ask, it is not.
 *
 * @param source The window to ask; the page's own by default.
 * @returns True on a touch page.
 */
export function isTouchPage(source: MediaWindow | undefined = typeof window === "undefined" ? undefined : window): boolean {
  return typeof source?.matchMedia === "function" && source.matchMedia(TOUCH_QUERY).matches;
}

/**
 * Calls back whenever the page becomes or stops being a touch page, as a mouse or a keyboard comes and goes.
 *
 * @param onChange Called with the new answer.
 * @param source The window to ask; the page's own by default.
 * @returns A function that stops watching.
 */
export function watchTouchPage(onChange: (touch: boolean) => void, source: MediaWindow | undefined = typeof window === "undefined" ? undefined : window): () => void {
  if (typeof source?.matchMedia !== "function") {
    return () => undefined;
  }
  const query = source.matchMedia(TOUCH_QUERY);
  /**
   * Passes the query's answer on.
   */
  const follow = (): void => onChange(query.matches);
  query.addEventListener("change", follow);
  return () => query.removeEventListener("change", follow);
}
